import React from 'react';

import { List } from '@mantine/core';

import { WorkoutSectionData } from './types';

interface WorkoutErrorSummaryItem {
  key: string;
  label: string;
}

const TOP_LEVEL_FIELDS = new Set(['name']);

/** Map `form.errors` (path -> message) to section-prefixed labels, ordered top-level first. */
function buildWorkoutErrorSummary(
  errors: Record<string, React.ReactNode>,
  sections: WorkoutSectionData[],
): WorkoutErrorSummaryItem[] {
  const items: (WorkoutErrorSummaryItem & { sortIndex: number })[] = [];

  Object.entries(errors).forEach(([path, rawMessage]) => {
    if (typeof rawMessage !== 'string' || rawMessage.trim().length === 0) return;

    if (TOP_LEVEL_FIELDS.has(path)) {
      items.push({ key: path, label: rawMessage, sortIndex: -1 });
      return;
    }

    const sectionMatch = path.match(/^sections\.(\d+)/);
    if (sectionMatch) {
      const sectionIndex = Number(sectionMatch[1]);
      const letter = sections[sectionIndex]?.letter ?? String(sectionIndex + 1);
      items.push({ key: path, label: `Section ${letter} — ${rawMessage}`, sortIndex: sectionIndex });
      return;
    }

    items.push({ key: path, label: rawMessage, sortIndex: Number.MAX_SAFE_INTEGER });
  });

  return items
    .sort((a, b) => (a.sortIndex !== b.sortIndex ? a.sortIndex - b.sortIndex : a.key.localeCompare(b.key)))
    .map(({ sortIndex: _sortIndex, ...item }) => item);
}

/** Show an error toast listing each validation problem by section when a save is blocked. */
export function notifyWorkoutValidationErrors(
  errorNotification: (title: React.ReactNode, message?: React.ReactNode) => void,
  errors: Record<string, React.ReactNode>,
  sections: WorkoutSectionData[],
  actionLabel: string,
): void {
  const items = buildWorkoutErrorSummary(errors, sections);
  const count = items.length;
  errorNotification(
    `Can't ${actionLabel} yet, please fix ${count} ${count === 1 ? 'issue' : 'issues'}`,
    <List size='sm' spacing={2}>
      {items.map(item => (
        <List.Item key={item.key}>{item.label}</List.Item>
      ))}
    </List>,
  );
}
